// Content for the three public policy pages. `info` comes from GET /api/legal.
const euros = (value) => `€${Number(value || 0).toLocaleString("en-IE", { maximumFractionDigits: 2 })}`;

export const LEGAL_DOCS = {
  privacy: {
    title: "Privacy Policy",
    kicker: "How we handle what you tell us",
    intro: (info) =>
      `${info.brand} is a service of ${info.entity}. ${info.entity} is the data controller for everything described below. We only collect what the service needs, we never sell it, and you can have it deleted at any time.`,
    sections: (info) => [
      {
        heading: "Who we are",
        body: [
          `${info.entity}, trading as ${info.brand}.`,
          info.address && `Registered address: ${info.address}`,
          info.registration && `Company registration: ${info.registration}`,
          info.vat_number && `VAT number: ${info.vat_number}`,
          info.contact_email && `Data protection contact: ${info.contact_email}`,
        ].filter(Boolean),
      },
      {
        heading: "What we collect",
        body: [
          "Booking details — your name, email address, chosen session, timezone and the notes you add.",
          "Pre-session assessment — the Big Five and Dark Triad answers you submit before a session, and your written description of the situation.",
          "Client portal — your account email, a hashed password (or a Google sign-in identifier), assessment scores, the incidents you log, your conversations with the AI advisor and the reports generated from them.",
          "Payment metadata — the amount, currency, status and Stripe identifiers of a payment. Card numbers are entered on Stripe's own checkout and never reach our servers.",
          "Attribution and usage — the campaign or link that brought you here, the pages you visited and the actions you took, stored first-party in our own database.",
        ],
      },
      {
        heading: "Why we are allowed to hold it",
        body: [
          "Performance of a contract — to run the session or subscription you paid for.",
          "Consent — for marketing emails and for contributing an anonymised version of your case to our research base. Both can be withdrawn at any time.",
          "Legitimate interest — to keep the service secure, to understand which campaigns work, and to improve the clinic's methods.",
          "Legal obligation — to keep invoices and tax records for the period the law requires.",
        ],
      },
      {
        heading: "AI processing",
        body: [
          "The AI advisor, the report writer and the session recap drafts run on Anthropic and OpenAI models through a processor agreement. Your text is sent to them to produce your answer and is not used to train their models.",
          "Nothing you write is reviewed by a human outside the clinic, and reports are never shared without a link you create yourself.",
        ],
      },
      {
        heading: "Research contributions",
        body: [
          "The clinic maintains an anonymous research base of leadership cases. If you do not opt out, a numbers-only version of your case is added: coded behaviours, trait ratings, country, occupation, age band and leader gender.",
          "No name, email, employer or free text is ever included, and your reference is a one-way hash. You can switch this off, or withdraw everything you have contributed, from the portal dashboard at any time.",
        ],
      },
      {
        heading: "Who else sees your data",
        body: [
          "Stripe (payments), Google Workspace (email and calendar), Anthropic and OpenAI (AI processing), Resend (backup email delivery) and our hosting provider. Each acts as a processor under contract.",
          "We never sell personal data and we do not share it with advertisers.",
        ],
      },
      {
        heading: "How long we keep it",
        body: [
          "Portal accounts and reports: until you delete your account, or after three years of inactivity.",
          "Booking and payment records: seven years, because tax law requires it.",
          "Marketing and attribution events: 24 months.",
        ],
      },
      {
        heading: "Your rights",
        body: [
          "You can ask for a copy of your data, correct it, have it deleted, restrict or object to how we use it, and receive it in a portable format.",
          info.contact_email
            ? `Email ${info.contact_email} and we will answer within 30 days.`
            : "Email the clinic and we will answer within 30 days.",
          "If you are in the EU or UK and you are not happy with our answer, you can complain to your national data protection authority.",
        ],
      },
      {
        heading: "Cookies and local storage",
        body: [
          "We use your browser's local storage to keep you signed in and to remember which campaign brought you here. Analytics are first-party and stored in our own database.",
          "We do not set third-party advertising cookies on this site.",
        ],
      },
      {
        heading: "International transfers",
        body: [
          "Some processors are based outside the EEA. Those transfers rely on the European Commission's standard contractual clauses.",
        ],
      },
    ],
  },

  terms: {
    title: "Terms of Service",
    kicker: "What you get, and what we ask of you",
    intro: (info) =>
      `These terms cover every purchase from ${info.brand}. ${info.entity} is the contracting party, issues the invoice and appears on your card statement as ${info.statement_descriptor}.`,
    sections: (info) => [
      {
        heading: "The service",
        body: [
          "One-to-one sessions with a behavioural scientist, a self-serve client portal with assessments, an AI advisor and a vulnerability report, and optional written case files.",
          "Sessions are delivered by video. You get the meeting link in your confirmation email and again in the reminder 24 hours before.",
        ],
      },
      {
        heading: "This is not therapy, medical or legal advice",
        body: [
          "The clinic provides evidence-based behavioural guidance about workplace situations. It is not psychotherapy, a medical service, or legal or HR representation, and it does not create a clinician-patient relationship.",
          "If you are in crisis, or at risk of harm, contact your local emergency service or a licensed health professional.",
          "Nothing here is a substitute for taking legal advice about your employment.",
        ],
      },
      {
        heading: "Who can buy",
        body: [
          "You must be 18 or older and buying for yourself. Employers commissioning sessions for staff should contact us directly.",
        ],
      },
      {
        heading: "Payment and prices",
        body: [
          "Prices are shown in euros and include any applicable VAT. Payment is taken at booking through Stripe.",
          `Your statement shows a discreet reference (${info.statement_descriptor}), not the clinic name.`,
          "Invoices are issued by " + info.entity + ".",
        ],
      },
      {
        heading: "Changing or cancelling",
        body: [
          `You can move a session yourself up to ${info.reschedule_cutoff_hours} hours before it starts, using the link in your confirmation email.`,
          "Refunds are covered in the Refund & Cancellation Policy.",
        ],
      },
      {
        heading: "Portal subscriptions",
        body: [
          "Premium renews automatically each month or year until you cancel. You can cancel at any time from the billing portal inside your account; access continues to the end of the period you have paid for.",
          "We may change the price of a future renewal with at least 30 days' notice by email.",
        ],
      },
      {
        heading: "Confidentiality",
        body: [
          "What you tell the clinic stays with the clinic. We only break that if we are legally compelled, or if there is a serious and immediate risk to someone's safety.",
          "Reports are private to you. A share link only exists if you create one, and you can revoke it instantly.",
        ],
      },
      {
        heading: "Acceptable use",
        body: [
          "Do not use the service to harass, dox or build a case against a named individual, to submit someone else's personal data without a lawful basis, or to resell our materials.",
          "We can suspend an account that does, and refund any unused balance.",
        ],
      },
      {
        heading: "AI limitations",
        body: [
          "Parts of the service are generated by AI from what you provide. It can be wrong, and it never replaces your own judgement about your workplace.",
          "Always sanity-check a suggested tactic against your contract, your employer's policies and your own safety.",
        ],
      },
      {
        heading: "Our materials",
        body: [
          "Reports, guides and session materials are licensed to you for personal use. The underlying research, models and content remain ours.",
        ],
      },
      {
        heading: "Liability",
        body: [
          "We are liable for the price you paid for the service in question. We are not liable for indirect losses such as lost earnings, lost employment or reputational harm.",
          "Nothing in these terms limits liability we cannot lawfully limit, including your statutory consumer rights.",
        ],
      },
      {
        heading: "Governing law",
        body: [
          info.jurisdiction
            ? `These terms are governed by the laws of ${info.jurisdiction}, and disputes go to its courts.`
            : `These terms are governed by the laws of the country in which ${info.entity} is established.`,
          "If you are a consumer, you keep the right to bring a claim in your own country of residence.",
        ],
      },
    ],
  },

  refunds: {
    title: "Refund & Cancellation Policy",
    kicker: "Plain numbers, no small print",
    intro: (info) =>
      `Plans change. Here is exactly what happens to your money in each case, applied automatically by the booking links in your confirmation email.`,
    sections: (info) => [
      {
        heading: "Moving a session",
        body: [
          `Free, any number of times, up to ${info.reschedule_cutoff_hours} hours before the start. Use the "Reschedule" link in your confirmation email.`,
          `Inside ${info.reschedule_cutoff_hours} hours the link stops working — email ${info.contact_email || "the clinic"} and we will do what we can.`,
        ],
      },
      {
        heading: "Cancelling a session",
        body: [
          `More than ${info.cancel_grace_hours} hours before the start: full refund, back to the card you paid with.`,
          `Inside ${info.cancel_grace_hours} hours: we refund the price minus a ${euros(info.cancel_fee_eur)} late-cancellation fee, because the slot can no longer be resold.`,
          "Refunds are issued to the original payment method and typically appear within 5–10 business days.",
        ],
      },
      {
        heading: "If you do not show up",
        body: [
          "The session is treated as delivered. We will email you a one-click link to rebook free of charge within 7 days, because life happens.",
        ],
      },
      {
        heading: "If we have to cancel",
        body: [
          "You choose: a full refund, or a rescheduled session at no cost. No fee applies.",
        ],
      },
      {
        heading: "Waiving the late fee",
        body: [
          `Emergencies are not billed. Tell us what happened at ${info.contact_email || "the clinic's email address"} and we can refund the ${euros(info.cancel_fee_eur)} fee.`,
        ],
      },
      {
        heading: "Premium subscriptions",
        body: [
          "Cancel any time from the billing portal in your account. Access runs to the end of the period you have paid for, and we do not charge you again.",
          "We do not refund part-months or part-years, and we do not charge an exit fee.",
        ],
      },
      {
        heading: "Written case files",
        body: [
          "Refundable in full until work starts on it. Once the file has been delivered it is non-refundable, because it is written specifically for you.",
        ],
      },
      {
        heading: "EU right of withdrawal",
        body: [
          "Consumers in the EU have 14 days to withdraw from a distance contract. By booking a session inside that window, or by generating a report, you ask us to start performance immediately and accept that the right lapses once the service has been fully delivered.",
          "Anything not yet delivered is still fully refundable within those 14 days.",
        ],
      },
    ],
  },
};

export const LEGAL_ORDER = ["privacy", "terms", "refunds"];
